/**
 * jitterBuffer — Adaptive snapshot ring buffer for network latency variance.
 *
 * Buffers incoming snapshots and outputs snapshot pairs for interpolation at a target delay.
 * Application layer only (no DOM/Three.js).
 *
 * Contrato público:
 *   createJitterBuffer({ targetDelayMs, maxCapacity }) → JitterBuffer
 *   JitterBuffer: push(snap), getInterpolationPair(localTime), clear(), getBufferedCount()
 */

/**
 * @param {{ targetDelayMs?: number, maxCapacity?: number }} [opts]
 */
export function createJitterBuffer({ targetDelayMs = 50, maxCapacity = 20 } = {}) {
  /** @type {object[]} */
  const buffer = [];

  function push(snapshot) {
    if (!snapshot) return;
    buffer.push(snapshot);
    // Keep sorted by serverTime
    buffer.sort((a, b) => (a.serverTime || 0) - (b.serverTime || 0));

    // Trim excess capacity
    while (buffer.length > maxCapacity) {
      buffer.shift();
    }
  }

  function getInterpolationPair(localTime) {
    if (buffer.length === 0) return null;
    const targetRenderTime = localTime - targetDelayMs;

    if (buffer.length === 1) {
      return { snapA: buffer[0], snapB: buffer[0], renderTime: targetRenderTime };
    }

    // Find snapA and snapB where snapA.serverTime <= targetRenderTime <= snapB.serverTime
    for (let i = 0; i < buffer.length - 1; i++) {
      const snapA = buffer[i];
      const snapB = buffer[i + 1];
      if ((snapA.serverTime || 0) <= targetRenderTime && targetRenderTime <= (snapB.serverTime || 0)) {
        return { snapA, snapB, renderTime: targetRenderTime };
      }
    }

    // If renderTime is newer than all buffered snapshots, use latest two
    if (targetRenderTime > (buffer[buffer.length - 1].serverTime || 0)) {
      return {
        snapA: buffer[buffer.length - 2],
        snapB: buffer[buffer.length - 1],
        renderTime: targetRenderTime
      };
    }

    // Otherwise use first two
    return {
      snapA: buffer[0],
      snapB: buffer[1],
      renderTime: targetRenderTime
    };
  }

  function clear() {
    buffer.length = 0;
  }

  function getBufferedCount() {
    return buffer.length;
  }

  return {
    push,
    getInterpolationPair,
    clear,
    getBufferedCount
  };
}
